import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { usePageTranslations } from "../i18n/context";
import {
  useCookieConsent,
  COOKIE_CONSENT_STORAGE_KEY as STORAGE_KEY,
  COOKIE_PREFERENCES_EVENT,
} from "../lib/cookieConsent";
import { disableTracking } from "../lib/analytics";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const { t } = usePageTranslations("common");
  const { setConsented } = useCookieConsent();

  useEffect(() => {
    try {
      const val = localStorage.getItem(STORAGE_KEY);
      if (val === "accepted" || val === "rejected") return;
    } catch { /* noop */ }
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Allow the footer "Cookie Preferences" control to re-open the banner at any time,
  // so consent can be changed or withdrawn as easily as it was given (Law 25).
  useEffect(() => {
    const reopen = () => setVisible(true);
    window.addEventListener(COOKIE_PREFERENCES_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_PREFERENCES_EVENT, reopen);
  }, []);

  const handleAccept = () => { setConsented(true); setVisible(false); };
  const handleReject = () => {
    setConsented(false);
    disableTracking();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:max-w-[380px] z-[55]"
          initial={{ y: 40, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 30, opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            className="rounded-2xl border border-[var(--color-border)] overflow-hidden"
            style={{
              background: "rgba(20,20,20,0.97)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              boxShadow: "0 12px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)",
            }}
          >
            {/* Top accent */}
            <div
              className="h-[2px]"
              style={{
                background: "linear-gradient(90deg, var(--color-brand-primary), var(--color-brand-tertiary) 50%, var(--color-brand-primary))",
                opacity: 0.6,
              }}
            />

            <div className="p-5 sm:p-6">
              {/* Cookie icon + Title */}
              <div className="flex items-center gap-2.5 mb-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    background: "rgba(74,122,181,0.1)",
                    border: "1px solid rgba(74,122,181,0.18)",
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--color-brand-primary)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
                    <path d="M8.5 8.5v.01" />
                    <path d="M16 15.5v.01" />
                    <path d="M12 12v.01" />
                    <path d="M11 17v.01" />
                    <path d="M7 14v.01" />
                  </svg>
                </div>
                <h3
                  className="text-[13px] font-bold text-white/90"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  {t("cookies.title")}
                </h3>
              </div>

              {/* Message */}
              <p
                className="text-[12px] text-white/55 leading-[1.7] mb-5"
                style={{ fontFamily: "'Geist Sans', sans-serif" }}
              >
                {t("cookies.message")}
              </p>

              {/* Buttons */}
              <div className="flex items-center gap-2.5">
                <button
                  onClick={handleReject}
                  className="flex-1 px-3 py-2 rounded-lg text-[9px] uppercase tracking-[0.08em] font-semibold text-white/50 hover:text-white/75 border border-[var(--color-border)] hover:border-[var(--color-brand-primary)] transition-all duration-200 cursor-pointer"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  {t("cookies.reject")}
                </button>
                <button
                  onClick={handleAccept}
                  className="flex-1 px-3 py-2 rounded-lg text-[9px] uppercase tracking-[0.08em] font-bold text-white transition-all duration-200 hover:brightness-110 cursor-pointer"
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    background: "var(--color-brand-primary)",
                  }}
                >
                  {t("cookies.accept")}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
